import logging from '@sstdev/lib_logging';
import { Card, Icon, View, fromTheme, h1, h2, styled, webOnlyStyles } from 'lib_ui-primitives';
import lodash from 'lodash';
import { createElement as rc, useMemo } from 'react';
import { useTheme } from 'styled-components';
import buildServerAggregationFilters from '../../hooks/buildServerAggregationFilters';
import useDbView from '../../hooks/useDbView';
import useFilterMetadata from '../../hooks/useFilterMetadata';
import useServerAggregation from '../../hooks/useServerAggregation';
import DownloadButton from '../actionElement/DownloadButton';
import FilterContainer from './FilterContainer';
const { sortBy } = lodash;

const ChartHeader = styled(Card.Header).attrs({ name: 'chart-header' })`
    padding: ${fromTheme('charts', 'header', 'padding')};
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${fromTheme('charts', 'header', 'gap')};
`;
ChartHeader.displayName = 'ChartHeader';
const HeaderButtons = styled(View).attrs({ name: 'header-buttons' })`
    align-items: flex-end;
    ${({ theme }) => (theme.selectedTheme === 'gl2025' ? '& button { margin: 0 !important; }' : '')};
`;
HeaderButtons.displayName = 'HeaderButtons';

const ChartBody = styled(Card.Body).attrs({ name: 'chart-body' })`
    padding: ${fromTheme('viewPadding')};
`;
ChartBody.displayName = 'ChartBody';

let Widget = styled(Card).attrs({ name: 'chart-widget' })`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    background-color: ${fromTheme('charts', 'backgroundColor')};
`;
Widget = webOnlyStyles(Widget)`
    box-shadow: ${fromTheme('charts', 'boxShadow')};
`;
Widget.displayName = 'ChartWidget';

const Title = styled(h1)`
    color: ${fromTheme('card', 'header', 'textColor')};
    ${fromTheme('charts', 'header', 'font')};
    margin: ${fromTheme('charts', 'header', 'titleMargin')};
    line-height: ${fromTheme('charts', 'header', 'title', 'lineHeight')};
`;

const Centered = styled(View).attrs({ name: 'centered-chart-body' })`
    flex-grow: 1;
    flex-shrink: 1;
    align-items: center;
    align-content: center;
    justify-content: center;
    flex-direction: column;
    padding: ${fromTheme('charts', 'totals', 'padding')};
`;

const NoRecords = styled(h2).attrs({ name: 'no-records' })`
    ${fromTheme('charts', 'header', 'font')};
    color: ${fromTheme('disabledFontColor')};
    text-align: center;
`;

const OfflineIconWrapper = styled(View).attrs({ name: 'offline-chart-icon' })`
    color: ${fromTheme('disabledFontColor')};
    align-items: center;
    justify-content: center;
`;
OfflineIconWrapper.displayName = 'OfflineIconWrapper';

const EMPTY_ARRAY = [];

const _p = { useFilterMetadata, useServerAggregation, useDbView, buildServerAggregationFilters };
export const _private = _p;

export default function Chart({ nestedComponent, ...props }) {
    const {
        children = EMPTY_ARRAY,
        hNode,
        hNode: { id, namespace, relation, limitResults = 21 }
    } = props;

    const hNodeChildren = hNode.children ?? EMPTY_ARRAY;
    const metadataBasedFilters = _p.useFilterMetadata(hNode);

    // Pre-compute whether filter controls exist from props (safe to read before hooks).
    // When filter controls are present they've already acquired the shared view via their
    // own useDbView calls, so subscribing here triggers no extra syncs. When there are
    // no filter controls there's nothing to read, so pass '__none' to skip data access.
    const hasFilterControls = children.some(
        child =>
            (child.props?.hNode?.hNodeTypeGroup === 'actionElement' &&
                child.props?.hNode?.hNodeType !== 'DownloadButton') ||
            child.props?.hNode?.hNodeTypeGroup === 'searchPane'
    );
    const { viewCriteria } = _p.useDbView(
        hasFilterControls ? namespace : '__none',
        hasFilterControls ? relation : '__none',
        undefined,
        hNode
    );
    // Mirror the grid: forward the shared route view's scoping filters (inventory membership via
    // nestedListEntry, includedValues, excludeDeleted, searchCriteria, navigation-selection
    // criteria, …) to the server aggregate so the chart is scoped the same way the grid is, instead
    // of aggregating every asset (#395). The chart's own groupBy/groupByBoolean ride along on
    // metadataBasedFilters and win over anything on the shared view.
    const sharedViewFilters = viewCriteria?.getFiltersFromView?.() ?? {};
    // getFiltersFromView() returns a fresh object every render. Key the memo on its serialization so
    // requestFilters keeps a value-stable reference — the aggregateResult useMemo below depends on it,
    // and without stability it would recompute on every render. (useServerAggregation's own internal
    // serialization only dedupes the network call; it doesn't give us a stable object here.)
    const sharedViewFiltersJson = JSON.stringify(sharedViewFilters);
    const requestFilters = useMemo(
        () => _p.buildServerAggregationFilters(metadataBasedFilters, JSON.parse(sharedViewFiltersJson)),
        [metadataBasedFilters, sharedViewFiltersJson]
    );

    const { data, isLoading, isOffline } = _p.useServerAggregation(namespace, relation, requestFilters);

    const theme = useTheme();

    const aggregateResult = useMemo(() => {
        logging.debug(`[CHART] Recalculating chart ${hNode.title} result`);
        if (!data) return EMPTY_ARRAY;
        const booleanFilter = requestFilters.groupByBoolean;
        const booleanLabels = booleanFilter
            ? { true: booleanFilter.trueLabel ?? 'Yes', false: booleanFilter.falseLabel ?? 'No' }
            : null;
        const mapValue = v => {
            if (v == null) return '';
            if (booleanLabels && typeof v === 'boolean') return booleanLabels[v] ?? String(v);
            return String(v);
        };
        // Unwind array _id.title values into separate groups, matching LokiJS groupBy behavior for array fields.
        // Duplicate titles within a single item are deduplicated; counts from different items with the same title are summed.
        const mergedMap = new Map();
        for (const item of data) {
            const raw = item._id?.title;
            const titles = Array.isArray(raw) ? [...new Set(raw.map(mapValue))] : [mapValue(raw)];
            for (const title of titles) {
                const existing = mergedMap.get(title);
                mergedMap.set(title, existing ? { ...existing, count: existing.count + item.count } : { ...item, _id: { title } });
            }
        }
        const normalized = [...mergedMap.values()];
        if (booleanLabels) {
            const presentTitles = new Set(normalized.map(item => item._id.title));
            if (!presentTitles.has(booleanLabels.true)) normalized.push({ _id: { title: booleanLabels.true }, count: 0 });
            if (!presentTitles.has(booleanLabels.false)) normalized.push({ _id: { title: booleanLabels.false }, count: 0 });
        }
        return sortBy(normalized, ['count']).reverse();
    }, [hNode, data, requestFilters]);

    const truncatedAggregateResult = useAggregateGroupByResults(aggregateResult || EMPTY_ARRAY, limitResults);

    const filterControls = useMemo(
        () =>
            children.filter(
                child =>
                    (child.props.hNode.hNodeTypeGroup === 'actionElement' &&
                        child.props.hNode.hNodeType !== 'DownloadButton') ||
                    child.props.hNode.hNodeTypeGroup === 'searchPane'
            ),
        [children]
    );

    const downloadButton = hNodeChildren.find(child => child.hNodeType === 'DownloadButton');

    let chartBody;
    if (isOffline) {
        chartBody = rc(OfflineIconWrapper, null, rc(Icon, { name: 'cloud-off', size: 'lg', color: 'currentColor' }));
    } else if (aggregateResult.length) {
        chartBody = rc(nestedComponent, { ...props, data: truncatedAggregateResult });
    } else if (isLoading) {
        chartBody = rc(NoRecords, { id }, 'Loading...');
    } else {
        chartBody = rc(NoRecords, { id }, `No ${hNode?.title || 'Records'}`);
    }

    // prettier-ignore
    return rc(Widget, null,
        rc(ChartHeader, null,
            rc(Title, null, hNode?.title),
            rc(HeaderButtons, null,
                rc(FilterContainer, null, filterControls),
                downloadButton && rc(DownloadButton, {
                    id: `download${id}`,
                    hNode: {
                        variant: 'secondary',
                        size:'sm',
                        ...downloadButton,
                        displayTitleOnButton: theme.selectedTheme === 'gl2025',
                        buttonStyle: downloadButton.style,
                        filePrefix: hNode?.title || `chart_${namespace}_${relation}`
                    },
                    data: aggregateResult
                })
            )
        ),
        rc(ChartBody, null,
            rc(Centered, null, chartBody)
        )
    );
}

function useAggregateGroupByResults(data, numberOfResults = 0, aggregateValueLabel = 'Other') {
    return useMemo(() => {
        return aggregateGroupByResults(data, numberOfResults, aggregateValueLabel);
    }, [data, numberOfResults, aggregateValueLabel]);
}

function aggregateGroupByResults(data, numberOfResults, aggregateValueLabel) {
    if (!Array.isArray(data)) {
        throw new Error('The data argument must be an array of Objects');
    }
    if (isNaN(+numberOfResults)) {
        throw new Error('the numberOfResults argument must be a number');
    }
    if (+numberOfResults === 0 || data.length <= +numberOfResults) {
        return data;
    }
    numberOfResults--;
    const displayedData = data.slice(0, numberOfResults);
    const aggregatedData = data.slice(numberOfResults);
    const aggregatedValue = aggregatedData.reduce((previousValue, currentValue) => {
        return previousValue + (currentValue.y ?? currentValue.count);
    }, 0);
    return [
        ...displayedData,
        {
            _id: { title: aggregateValueLabel },
            name: aggregateValueLabel,
            y: aggregatedValue
        }
    ];
}
